<template>
	<div class="customers-display">
		<div
			v-if="currCustomer"
			class="customers-row"
		>
			<div
				class="customer-data-container"
				:title="getString('edit_x', [getString(currCustomer?.type === 'company' ? 'company' : 'person')])"
				:class="{pointer: showEditableCustomer}"
				@click="clickCustomerHeading"
			>
				<div class="customer-data-edit" v-if="showEditableCustomer">
					<div class="customer-icon" v-html="svgStore.get('iconEdit')"></div>
				</div>
				<div class="customers-data">
					<div class="customer-name"><p>{{ currCustomer?.name ? currCustomer?.name : '-' }}</p></div>
					<div class="customer-type">{{ getString(currCustomer?.type) }} <span v-if="counterStore?.invoiceAdditionalData?.purchase_order"> - {{getString('purchase_order_x', [counterStore?.invoiceAdditionalData?.purchase_order])}}</span></div>
				</div>
				<div class="customer-icon bin-icon" :title="getString('remove')" v-html="svgStore.get('iconBin')" v-if="!notEditable" @click.stop="removeCustomer"></div>
			</div>
			<div class="credit-container" :title="getString('add_credit')" :class="{credit: (currCustomer?.credit || 0) > 0, pointer: !counterStore?.creditTopUpMode && !notEditable}" @click.stop="clickCredit">
				<div class="credit-container-label">
					<p>{{ getString('credit_x', [currencyStore.currCurrency.symbol]) }}</p>
<!--					<div class="icon-credit" v-html="svgStore.get('iconCustomerCredit')"></div>-->
				</div>
				<p class="credit-container-value">{{ readableCustomerCredit }}</p>
			</div>
		</div>
		<AppBottomSheet :forceWidth="bottomSheetWidth" :active="activeEditInvoiceInfo" @toggling-popup="togglingPopupInvoiceInfo">
			<div class="wrapper-buttons-add mb-10">
				<ModalHeader
					:title="getString('invoice_x', [this.currCustomer ? this.currCustomer?.name : '-'])"
				/>
				<AppList :items="editInvoiceInfoList" />
			</div>
		</AppBottomSheet>
		<AppBottomSheet :forceWidth="bottomSheetWidth" :active="activeEditInvoiceData" @toggling-popup="togglingPopupInvoiceData">
			<div class="wrapper-buttons-add mb-10" v-if="activeEditInvoiceData">
				<ModalHeader
					:title="getString('edit_invoice_x', [this.currCustomer ? this.currCustomer?.name : '-'])"
					:button-text="getString('confirm')"
					@clickButton="updateInvoiceData"
				/>
				<InvoiceDataEditor ref="invoice-data-editor" :starting-purchase-order="invoicePurchaseOrder" />
			</div>
		</AppBottomSheet>
		<CustomersPersonDetails v-if="!!personId" ref="person-details" :person-id="personId" @deleted-customer="emitDeletedPersonCustomer"  @toggling-popup="togglingPopupPersonDetails" />
		<CustomersCompanyDetails v-if="!!companyId" ref="company-details" :company-id="companyId" @deleted-company="emitDeletedCompanyCustomer" @toggling-popup="togglingPopupCompanyDetails" />
	</div>
</template>

<script>
import AppButton from "@/shared/components/AppButton.vue";
import AppList from "@/shared/components/AppList.vue";
import {useCurrencyStore, useLanguageStore, useSvgStore} from "@/shared/stores";
import {useCounterStore, useCustomersStore} from "@/console-new/stores";
import AppButtonIcon from "@/shared/components/AppButtonIcon.vue";
import CustomersCompanyAdd from "@/console-new/components/ops/CustomersCompanyAdd.vue";
import AppBottomSheet from "@/shared/components/AppBottomSheet.vue";
import WindowWidth from "@/console-new/mixins/WindowWidth";
import CustomersPersonEditDetails from "@/console-new/components/ops/CustomersPersonEditDetails.vue";
import ModalHeader from "@/console-new/components/shared/ModalHeader.vue";
import CustomersCompanyEditDetails from "@/console-new/components/ops/CustomersCompanyEditDetails.vue";
import InvoiceDataEditor from "@/console-new/components/ops/InvoiceDataEditor.vue";
import AppLoader from "@/shared/components/AppLoader.vue";
import BottomNumberEditor from "@/console-new/components/ops/counter/BottomNumberEditor.vue";
import {defineAsyncComponent} from "vue";

export default {
	setup() {
		const counterStore = useCounterStore();
		const customersStore = useCustomersStore();
		const svgStore = useSvgStore();
		const languageStore = useLanguageStore();
		const currencyStore = useCurrencyStore();
		return {counterStore, svgStore, currencyStore, getString: languageStore.getString, customersStore};
	},
	name: "CustomersDisplay",
	mixins: [WindowWidth],
	emits: ['deleted-customer', 'deleted-company'],
	components: {
		BottomNumberEditor,
		AppLoader, ModalHeader, AppBottomSheet, InvoiceDataEditor, CustomersCompanyAdd, AppList, AppButtonIcon, AppButton, CustomersPersonEditDetails, CustomersCompanyDetails: defineAsyncComponent(() => import("@/console-new/components/ops/CustomersCompanyDetails.vue")), CustomersPersonDetails: defineAsyncComponent(() => import("@/console-new/components/ops/CustomersPersonDetails.vue")), CustomersCompanyEditDetails},
	data() {
		return {
			personId: null,
			companyId: null,
			activeEditInvoiceInfo: false,
			activeEditInvoiceData: false,
		}
	},
	computed: {
		readableCustomerCredit() {
			let symbol = Number(this.currCustomer?.credit || 0) > 0 ? '+' : '';
			return symbol + this.currencyStore.readableCurrencyValue(this.currCustomer?.credit, true, false);
		},
		bottomSheetWidth() {
			if(this.windowWidth < 550) {
				return "100%";
			} else if(this.windowWidth < 768) {
				return "550px";
			} else {
				return "720px";
			}
		},
		currCustomer() {
			if(this.isRegistry) {
				return this.counterStore?.registryEditingCustomer;
			}
			return this.counterStore?.selectedCustomer;
		},
		editInvoiceInfoList() {
			let list = [];
			if(!this.notEditable) {
				list.push({title: this.getString('edit_x', [this.getString('company')]), buttonTitle: this.getString('edit'), callbackButton: this.editCustomer});
			}
			list.push({title: this.getString('edit_x', [this.getString('invoice')]), buttonTitle: this.getString('edit'), callbackButton: this.editInvoiceData});
			return list;
		},
		invoicePurchaseOrder() {
			return this.counterStore.invoiceAdditionalData?.purchase_order || null;
		},
		showEditableCustomer() {
			return !this.counterStore?.creditTopUpMode && (!this.notEditable || this.currCustomer?.type === 'company');
		},
		clickCustomerHeading() {
			if(this.counterStore?.creditTopUpMode) { return }
			if(!this.notEditable && this.currCustomer?.type !== 'company') {
				return this.editCustomer;
			} else if(this.currCustomer?.type === 'company') {
				return this.editCustomerCompany;
			}
		}
	},
	methods: {
		removeCustomer() {
			this.counterStore.selectedCustomer = null;
		},
		editInvoiceData() {
			this.activeEditInvoiceData = true;
		},
		editCustomerCompany() {
			this.activeEditInvoiceInfo = true;
		},
		clickCredit() {
			console.log('click credit', 'notEditable:', this.notEditable, 'creditTopUpMode:', this.counterStore?.creditTopUpMode);
			if(!this.notEditable) {
				this.editCustomer();
			}
			this.$nextTick(()=>{
				if(!this.notEditable && this.currCustomer?.type !== 'company') {
					requestAnimationFrame(() => {
						requestAnimationFrame(() => {
							let personDetails = this.$refs["person-details"];
							if(personDetails) {
								personDetails.cart = [];
								personDetails.tabIndex = 0;
								personDetails.tabButtonAction();
							}
						});
					});
				} else if(this.currCustomer?.type === 'company') {
					requestAnimationFrame(() => {
						requestAnimationFrame(() => {
							let companyDetails = this.$refs["company-details"];
							if(companyDetails) {
								companyDetails.cart = [];
								companyDetails.tabIndex = 0;
								companyDetails.tabButtonAction();
							}
						});
					});
				}
			})
		},
		editCustomer() {
			if(this.counterStore.selectedCustomer) {
				if(this.currCustomer?.type === "person") {
					this.customersStore.customersPeople = [this.counterStore.selectedCustomer];
					this.personId = this.currCustomer?.customer_id;
				} else if(this.currCustomer?.type === "company") {
					this.customersStore.customersCompanies = [this.counterStore.selectedCustomer];
					this.companyId = this.currCustomer?.customer_id;
				}
			}
		},
		updateInvoiceData() {
			if(this.currCustomer?.type === "company") {
				let invoiceDataEditor = this.$refs["invoice-data-editor"];
				if(invoiceDataEditor && invoiceDataEditor?.toUpdate) {
					let additionalInvoiceData = invoiceDataEditor.getData();
					if(additionalInvoiceData) {
						this.counterStore.invoiceAdditionalData.purchase_order = additionalInvoiceData.purchase_order;
					}
				}
			}
			this.activeEditInvoiceData = false;
		},
		togglingPopupPersonDetails(isOpen) {
			if(!isOpen) {
				this.personId = null;
			}
		},
		togglingPopupInvoiceInfo(isOpen) {
			if(!isOpen) {
				this.activeEditInvoiceInfo = false;
			}
		},
		togglingPopupInvoiceData(isOpen) {
			if(!isOpen) {
				this.activeEditInvoiceData = false;
			}
		},
		togglingPopupCompanyDetails(isOpen) {
			if(!isOpen) {
				this.companyId = null;
			}
		},
		emitDeletedPersonCustomer(personId) {
			this.$emit('deleted-customer', personId);
			this.personId = null;
		},
		emitDeletedCompanyCustomer(companyId) {
			this.$emit('deleted-company', companyId);
			this.companyId = null;
		},
	},
	props: {
		notEditable: {
			type: Boolean,
			default: false,
		},
		isRegistry: {
			type: Boolean,
			default: false,
		},
		isOrders: {
			type: Boolean,
			default: false,
		},
	},
	watch: {
		'counterStore.selectedCustomer'(newValue) {
			if(newValue?.type !== 'company') {
				this.counterStore.invoiceAdditionalData = {
					purchase_order: null,
					payment_due_date: null,
				}
			}
		}
	}
}
</script>

<style scoped lang="scss">
.customers-display {
	width: 100%;

	.customers-row {
		display: flex;
		flex-direction: row;
		justify-content: space-between;
		align-items: center;
		gap: 10px;
		width: 100%;
		margin-top: 10px;
		margin-bottom: 10px;

		.customer-data-container {
			display: flex;
			flex-direction: row;
			justify-content: flex-start;
			align-items: center;
			flex: 1;
			min-width: 0;
			padding: 6px 10px;
			border: 1px solid transparent;
			background-color: var(--color-bg-primary-2);
			border-radius: 13px;
			&.pointer {
				border-color: var(--color-primary);
			}

			.customer-data-edit {
				display: flex;
				justify-content: center;
				align-items: center;
				user-select: none;
				cursor: pointer;
				margin-right: 8px;
			}

			.customers-data {
				display: flex;
				flex-direction: column;
				justify-content: center;
				align-items: flex-start;
				flex: 1;
				min-width: 0;

				.customer-name {
					display: block;
					max-width: 100%;
					p {
						max-width: 100%;
						font-size: 16px;
						font-weight: 500;
						@include truncateString(1);
					}
				}

				.customer-type {
					max-width: 100%;
					font-size: 12px;
					color: $txt_2;

					span {
						@include truncateString;
						display: block;
						font-size: 12px;
						color: $txt_2;
						line-height: inherit;
					}
				}
			}

			.customer-icon {
				width: 24px;
				height: 24px;
				display: flex;
				justify-content: center;
				align-items: center;
				user-select: none;
				cursor: pointer;
				padding: 4px;

				:deep(svg) {
					width: 100%;
					height: 100%;
					.fill {
						fill: var(--color-svg-primary);
					}
					.stroke {
						stroke: var(--color-svg-primary);
					}
				}

				&.bin-icon {
					margin-left: auto;
					width: 32px;
					height: 32px;
					background-color: var(--color-display-alpha-blue);
					border-radius: 8px;
					padding: 8px;
				}
			}
		}

		.credit-container {
			display: flex;
			flex-direction: column;
			justify-content: center;
			align-items: center;
			border-radius: 13px;
			width: 100px;
			min-width: 100px;
			height: 51.5px;
			text-align: center;
			background-color: var(--color-alpha-1);
			flex-shrink: 0;

			&.pointer {
				border: 1px solid var(--color-primary);
			}
			&.debit {
				background-color: var(--color-display-alpha-red);
			}
			&.credit {
				background-color: var(--color-display-alpha-green);
			}

			.credit-container-label {
				display: flex;
				flex-direction: row;
				justify-content: center;
				align-items: center;
				p {
					font-size: 12px;
					line-height: 16px;
				}

				.icon-credit {
					margin-left: 4px;
					display: flex;
					justify-content: center;
					align-items: center;
					:deep(svg) {
						width: 10px;
						height: 10px;

						.fill {
							fill: var(--color-txt);
						}

						.stroke {
							stroke: var(--color-txt);
						}
					}
				}
			}

			p {
				max-width: 100%;
				@include truncateString(1);

				&.credit-container-value {
					font-size: 16px;
				}
			}
		}
	}
}
.wrapper-buttons-add {
	height: 100%;
	width: 100%;
	.row-button-add {
		display: flex;
		flex-direction: row;
		justify-content: space-between;
		align-items: center;
		width: 100%;
	}
	.base-button {
		height: 40px;
		width: calc(50% - 5px);
		background-color: var(--color-fill-button);
		border: none;
		&:hover {
			box-shadow: none;
			background-color: var(--color-fill-button-hover);
		}
	}
}
</style>